import {
  type BankAccount,
  type BillingEntityOption,
  type GstPlaceOfSupply,
  type InvoiceFormOptions,
} from '@utazzo/shared';
import { UzCard, UzCheckbox, UzInput, UzSelect, type UzSelectOption } from '@utazzo/ui';
import React, { useMemo } from 'react';

import {
  BILL_TYPE_OPTIONS,
  PRODUCT_TYPE_OPTIONS,
  slabUsesBillType,
  type GstSlab,
  type ProductType,
} from './invoice-form.helpers';

import { useStateOptions } from '@/features/branches/use-branches';
import { useHomeCountryId } from '@/features/leads/use-lead-options';

/** Place-of-supply choices; AUTO = derive from the customer's state/country. */
const PLACE_OF_SUPPLY_OPTIONS: ReadonlyArray<UzSelectOption<GstPlaceOfSupply | 'AUTO'>> = [
  { value: 'AUTO', label: 'Auto (from customer)' },
  { value: 'INTRA', label: 'Intra-state (CGST + SGST)' },
  { value: 'INTER', label: 'Inter-state (IGST)' },
  { value: 'EXPORT', label: 'Export / Out of India (0%)' },
];

interface ProductGstSectionProps {
  options: InvoiceFormOptions | undefined;
  productType: ProductType;
  onProductType: (v: ProductType) => void;
  selectedProducts: string[];
  onToggleProduct: (code: string) => void;
  gstSlab: GstSlab | '';
  onGstSlab: (v: GstSlab) => void;
  placeOfSupply: GstPlaceOfSupply | '';
  onPlaceOfSupply: (v: GstPlaceOfSupply | '') => void;
  showGstBifurcation: boolean;
  onShowGstBifurcation: (v: boolean) => void;
  billType: string;
  onBillType: (v: string) => void;
  hotelLocationStateId: string;
  onHotelLocationStateId: (v: string) => void;
  isGroupTour: boolean;
  onIsGroupTour: (v: boolean) => void;
  groupTourName: string;
  onGroupTourName: (v: string) => void;
  billingEntities: readonly BillingEntityOption[] | undefined;
  billingEntityId: string;
  onBillingEntityId: (v: string) => void;
  bankAccounts: readonly BankAccount[] | undefined;
  bankAccountId: string;
  onBankAccountId: (v: string) => void;
  buyerGstin: string;
  onBuyerGstin: (v: string) => void;
  buyerGstinRequired: boolean;
  buyerGstinMissing: boolean;
  showErrors: boolean;
  slabMissing: boolean;
  productsMissing: boolean;
}

/**
 * Section 1 - Product Info + GST slab. Product Type drives the product list;
 * the GST slab drives whether a Bill Type (with/without service charge)
 * control appears; a Hotel Location state select appears only when the sole
 * selected product is HOTEL and the trip is domestic.
 */
export const ProductGstSection = ({
  options,
  productType,
  onProductType,
  selectedProducts,
  onToggleProduct,
  gstSlab,
  onGstSlab,
  placeOfSupply,
  onPlaceOfSupply,
  showGstBifurcation,
  onShowGstBifurcation,
  billType,
  onBillType,
  hotelLocationStateId,
  onHotelLocationStateId,
  isGroupTour,
  onIsGroupTour,
  groupTourName,
  onGroupTourName,
  billingEntities,
  billingEntityId,
  onBillingEntityId,
  bankAccounts,
  bankAccountId,
  onBankAccountId,
  buyerGstin,
  onBuyerGstin,
  buyerGstinRequired,
  buyerGstinMissing,
  showErrors,
  slabMissing,
  productsMissing,
}: ProductGstSectionProps): React.JSX.Element => {
  const products = options?.products[productType] ?? [];
  const soleProductIsHotel = selectedProducts.length === 1 && selectedProducts[0] === 'HOTEL';
  const showHotelLocation = soleProductIsHotel && productType === 'DOM';

  const homeCountryId = useHomeCountryId();
  const stateQuery = useStateOptions(showHotelLocation ? homeCountryId : null);
  const stateOptions = useMemo<ReadonlyArray<UzSelectOption>>(
    () => (stateQuery.data ?? []).map((s) => ({ value: s.id, label: s.name })),
    [stateQuery.data],
  );

  const slabOptions = useMemo<ReadonlyArray<UzSelectOption<GstSlab>>>(
    () => (options?.gstSlabs ?? []).map((s) => ({ value: s.code as GstSlab, label: s.name })),
    [options?.gstSlabs],
  );

  const billingEntityOptions = useMemo<ReadonlyArray<UzSelectOption>>(
    () =>
      (billingEntities ?? []).map((e) => ({ value: e.id, label: `${e.legalName} - ${e.gstin}` })),
    [billingEntities],
  );

  return (
    <UzCard title="Product info" description="What is being sold and how it is taxed.">
      <div className="space-y-5">
        <div className="grid gap-4 md:grid-cols-3">
          <UzSelect<ProductType>
            label="Product type"
            options={PRODUCT_TYPE_OPTIONS}
            value={productType}
            onValueChange={onProductType}
          />
          <UzSelect<GstSlab>
            label="GST slab"
            required
            placeholder="Select a slab"
            options={slabOptions}
            value={gstSlab || undefined}
            onValueChange={onGstSlab}
            error={showErrors && slabMissing ? 'Choose a GST slab' : undefined}
          />
          <UzSelect<GstPlaceOfSupply | 'AUTO'>
            label="Place of supply"
            hint="Auto derives it from the customer's state/country. Override to force intra / inter / export."
            options={PLACE_OF_SUPPLY_OPTIONS}
            value={placeOfSupply || 'AUTO'}
            onValueChange={(v) => onPlaceOfSupply(v === 'AUTO' ? '' : v)}
          />
          {slabUsesBillType(gstSlab) && (
            <UzSelect
              label="Bill type"
              hint="Service charge treatment for B2C / B2CF."
              options={BILL_TYPE_OPTIONS}
              value={billType || undefined}
              placeholder="Select bill type"
              onValueChange={onBillType}
            />
          )}
          <UzSelect
            label="GST account"
            hint="The seller entity this invoice is raised under."
            options={billingEntityOptions}
            value={billingEntityId || undefined}
            placeholder="Select GST account"
            onValueChange={onBillingEntityId}
          />
          <UzSelect
            label="Bank account (optional)"
            hint="Leave blank to print all company banks; pick one to print only that bank."
            options={[
              { value: '', label: 'All company banks' },
              ...(bankAccounts ?? []).map((b) => ({
                value: b.id,
                label: `${b.label} - ${b.bankName} (${b.accountNumber})`,
              })),
            ]}
            value={bankAccountId || undefined}
            placeholder="All company banks"
            onValueChange={onBankAccountId}
          />
          <UzInput
            label={`Customer GSTIN${buyerGstinRequired ? ' *' : ''}`}
            className="font-mono"
            placeholder="Customer's GSTIN"
            value={buyerGstin}
            onChange={(e) => onBuyerGstin(e.target.value)}
            error={
              showErrors && buyerGstinMissing
                ? 'Customer GSTIN is required for corporate'
                : undefined
            }
          />
        </div>

        <UzCheckbox
          label="Show GST breakup on invoice"
          description="Print the CGST / SGST / IGST bifurcation on the invoice (defaults on for corporate, off for retail)."
          checked={showGstBifurcation}
          onCheckedChange={(c) => onShowGstBifurcation(Boolean(c))}
        />

        <div>
          <label className="mb-2 block text-xs font-medium tracking-wide text-zinc-500 uppercase">
            Products{' '}
            {showErrors && productsMissing && (
              <span className="text-red-600">- select at least one</span>
            )}
          </label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {products.map((p) => (
              <UzCheckbox
                key={p.code}
                label={p.name}
                checked={selectedProducts.includes(p.code)}
                onCheckedChange={() => onToggleProduct(p.code)}
              />
            ))}
          </div>
        </div>

        {showHotelLocation && (
          <div className="grid gap-4 md:grid-cols-3">
            <UzSelect
              label="Hotel location (state)"
              placeholder={stateQuery.isLoading ? 'Loading...' : 'Select state'}
              options={stateOptions}
              value={hotelLocationStateId || undefined}
              onValueChange={onHotelLocationStateId}
              disabled={stateQuery.isLoading}
            />
          </div>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <UzCheckbox
              label="Group tour"
              checked={isGroupTour}
              onCheckedChange={(c) => onIsGroupTour(Boolean(c))}
            />
            {isGroupTour && (
              <UzInput
                label="Group name"
                value={groupTourName}
                onChange={(e) => onGroupTourName(e.target.value)}
              />
            )}
          </div>
        </div>
      </div>
    </UzCard>
  );
};
